import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { phpEmployeeList } from "../data/api";
import { managerPinMatches } from "../engine/ageVerify";

// Android showManagerPinVerificationDialog: 4 PIN dots + a 3×4 keypad (1-9, blank, 0, ⌫). The 4th
// digit submits automatically; the PIN is checked LOCALLY against the store's employee list
// (PasscodeViewModel.setPasscodeRequestLocal(pin, ROLE_MANAGER)) — a wrong PIN shakes the dots and
// toasts "Invalid Passcode". ✕ returns to the ID-scan dialog (onClose).
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];

export default function ManagerPinModal({ onSuccess, onClose }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const employeesRef = useRef(null); // fetched once per dialog open (the POS reads its Room cache)
  const rootRef = useRef(null);

  useEffect(() => {
    let alive = true;
    phpEmployeeList()
      .then((r) => {
        if (alive) employeesRef.current = Array.isArray(r && r.result) ? r.result : [];
      })
      .catch(() => {
        if (alive) employeesRef.current = [];
      });
    if (rootRef.current) rootRef.current.focus();
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (value) => {
    setBusy(true);
    try {
      if (employeesRef.current == null) {
        // list still loading — wait for it (bounded) rather than refusing a valid PIN
        for (let i = 0; i < 40 && employeesRef.current == null; i++) await new Promise((r) => setTimeout(r, 100));
      }
      if (managerPinMatches(employeesRef.current || [], value)) {
        onSuccess();
        return;
      }
      setShake(true);
      setTimeout(() => setShake(false), 450);
      ToastifyAlert("Invalid Passcode", "error");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const press = (k) => {
    if (busy) return;
    if (k === "⌫") return setPin((p) => p.slice(0, -1));
    if (!/^[0-9]$/.test(k) || pin.length >= 4) return;
    const next = pin + k;
    setPin(next);
    if (next.length === 4) submit(next);
  };
  const onKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) press(e.key);
    else if (e.key === "Backspace") press("⌫");
    else return;
    e.preventDefault();
  };

  return (
    <Modal title="Manager Passcode" onClose={onClose} width={360} zIndex={2360}>
      <div ref={rootRef} tabIndex={-1} onKeyDown={onKeyDown} style={{ outline: "none" }}>
        <div style={sx.lead}>Please Enter Manager PIN for Verification</div>
        <div style={{ ...sx.dots, animation: shake ? "qv-pin-shake 0.4s" : "none" }} aria-label={`${pin.length} of 4 digits entered`}>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} style={{ ...sx.dot, background: i < pin.length ? "var(--accent)" : "var(--panel-2)", borderColor: i < pin.length ? "var(--accent)" : "var(--line)" }} />
          ))}
        </div>
        <div className="qv-keys" role="group" aria-label="PIN keypad">
          {KEYS.map((k, i) =>
            k === "" ? (
              <span key={i} aria-hidden="true" />
            ) : (
              <button key={i} type="button" className={k === "⌫" ? "qv-btn qv-key qv-btn--ghost" : "qv-btn qv-key qv-num"} onClick={() => press(k)} aria-label={k === "⌫" ? "Delete last digit" : k} disabled={busy}>
                {k}
              </button>
            )
          )}
        </div>
        <style>{`@keyframes qv-pin-shake{10%,90%{transform:translateX(-2px)}20%,80%{transform:translateX(4px)}30%,50%,70%{transform:translateX(-6px)}40%,60%{transform:translateX(6px)}}`}</style>
      </div>
    </Modal>
  );
}

const sx = {
  lead: { fontSize: 14, color: "var(--ink-2)", textAlign: "center" },
  dots: { display: "flex", justifyContent: "center", gap: 14, margin: "18px 0 16px" },
  dot: { width: 18, height: 18, borderRadius: "50%", border: "2px solid", display: "inline-block" },
};
